export const TERMS_VERSION = "2026-07-06-v1";

export const TERMS_ACCEPTANCE_COPY = {
  headingAfterRole: "Platform Terms",
  agreeBeforeRole: "I have read and agree to the AxioHire",
  agreeAfterRole: "Platform Terms",
  versionPrefix: "Version",
  viewFullTerms: "View full terms",
  reviewedCta: "I’ve reviewed these terms",
  openToContinue: "Open and review the full terms to continue",
  modalTitleBeforeRole: "AxioHire",
} as const;

export interface TermsSection {
  id: string;
  title: string;
  bullets: string[];
}

export interface RoleTerms {
  roleLabel: string;
  intro: string;
  sections: TermsSection[];
  conflictOfInterest?: {
    title: string;
    body: string;
    checkboxLabel: string;
  };
}

export function termsModalTitle(terms: RoleTerms): string {
  return `${TERMS_ACCEPTANCE_COPY.modalTitleBeforeRole} ${terms.roleLabel} ${TERMS_ACCEPTANCE_COPY.agreeAfterRole}`;
}

export function termsHeading(terms: RoleTerms): string {
  return `${terms.roleLabel} ${TERMS_ACCEPTANCE_COPY.headingAfterRole}`;
}

export function termsAgreeLabel(terms: RoleTerms, version: string): string {
  return `${TERMS_ACCEPTANCE_COPY.agreeBeforeRole} ${terms.roleLabel} ${TERMS_ACCEPTANCE_COPY.agreeAfterRole} (${version})`;
}

export const recruiterTerms: RoleTerms = {
  roleLabel: "Recruiter",
  intro:
    "These terms govern your use of AxioHire as an independent recruiter. By joining, you agree to uphold candidate integrity, data protection, and fair marketplace conduct.",
  sections: [
    {
      id: "contractor",
      title: "Independent contractor status",
      bullets: [
        "You are an independent contractor, not an employee of AxioHire or any client company.",
        "You control your own methods, hours, and sourcing approach; the platform does not mandate exclusivity.",
        "You are responsible for your own taxes and statutory obligations applicable to independent contractors.",
      ],
    },
    {
      id: "integrity",
      title: "Candidate integrity",
      bullets: [
        "You must not submit fake, fabricated, or misrepresented candidate profiles.",
        "Screening evidence (voice clips, typing tests, portfolios) must be genuine and match the candidate submitted.",
        "Misrepresentation may result in submission rejection, payout withholding, and account suspension.",
      ],
    },
    {
      id: "consent",
      title: "Consent & data protection (DPDP)",
      bullets: [
        "You must obtain explicit candidate consent before submitting personal data to the platform.",
        "Use candidate data only for recruitment purposes the candidate has consented to.",
        "Consent records must be maintained and produced on audit or dispute.",
      ],
    },
    {
      id: "dedup",
      title: "De-duplication & attribution",
      bullets: [
        "First valid submission rules apply; duplicate submissions to the same client/role may be blocked.",
        "You must not game attribution by re-submitting the same candidate under different details.",
        "Platform audit logs determine submission credit in disputes.",
      ],
    },
    {
      id: "circumvention",
      title: "No circumvention",
      bullets: [
        "You must not arrange off-platform placements with clients or candidates introduced via AxioHire to avoid fees.",
        "Client job descriptions, fee terms, and pipeline data are confidential.",
        "Violations may result in forfeiture of pending payouts and permanent removal.",
      ],
    },
    {
      id: "payouts",
      title: "Payouts & quality",
      bullets: [
        "Payouts are tied to confirmed candidate joining and applicable guarantee windows.",
        "Payouts may be withheld for fraud, fake candidates, policy breaches, excessive no-shows, or client non-payment per platform terms.",
        "TDS and other statutory deductions apply as per applicable law.",
      ],
    },
    {
      id: "kyc",
      title: "KYC & identity",
      bullets: [
        "You must provide truthful KYC and banking details; one recruiter identity per person.",
        "Account sharing or impersonation is prohibited.",
        "Fraudulent banking details will be reported and payouts frozen.",
      ],
    },
    {
      id: "enforcement",
      title: "Quality & enforcement",
      bullets: [
        "AxioHire may audit submissions and recruiter activity at any time.",
        "High dispute rates, replacement rates, or no-join patterns may lead to restrictions.",
        "Material changes to these terms require re-acceptance before continued use.",
      ],
    },
  ],
};

export const hrTerms: RoleTerms = {
  roleLabel: "HR / Company",
  intro:
    "These terms govern your use of AxioHire as a company hiring representative. You agree to post legitimate roles, protect candidate data, and use the platform fairly.",
  conflictOfInterest: {
    title: "Conflict of interest — required acknowledgment",
    body:
      "You must not register on AxioHire as a recruiter, assist others to register as recruiters for your company's roles, or personally submit candidates to your own company's job postings. This is a conflict of interest. If found in violation, your access will be reported, revoked, and any applicable placement fees or payouts may be withheld.",
    checkboxLabel:
      "I acknowledge the conflict-of-interest policy and confirm I will not register as a recruiter or submit candidates to my own company's roles",
  },
  sections: [
    {
      id: "authority",
      title: "Role & authority",
      bullets: [
        "You represent a legitimate hiring company and have authority to post roles and review candidates.",
        "Information you provide (company name, job details, CTC bands) must be accurate and current.",
        "Misrepresentation may result in access revocation and invoicing disputes.",
      ],
    },
    {
      id: "conflict",
      title: "Conflict of interest",
      bullets: [
        "You must not maintain a separate recruiter account or submit candidates to your own company's roles.",
        "You must not encourage employees or vendors to register as recruiters to source for your roles outside platform rules.",
        "Violations will be investigated; fees may be withheld and accounts terminated.",
      ],
    },
    {
      id: "postings",
      title: "Job postings & platform use",
      bullets: [
        "All job postings must represent real, open positions with accurate location, CTC, and job descriptions.",
        "Bait-and-switch roles, fake openings, or misleading requirements are prohibited.",
        "AxioHire may pause or remove postings that violate quality standards.",
      ],
    },
    {
      id: "data",
      title: "Candidate data (DPDP)",
      bullets: [
        "Candidate data received via the platform must be used only for hiring the relevant role.",
        "No scraping, resale, or off-platform sharing without valid consent.",
        "You are responsible for lawful handling of personal data under applicable Indian law.",
      ],
    },
    {
      id: "fees",
      title: "Fees & payment",
      bullets: [
        "Placement fees apply per your company's agreement with AxioHire upon successful hire.",
        "Direct hiring of platform-introduced candidates to circumvent fees is prohibited.",
        "Overdue invoices may result in paused access to new submissions or placements.",
      ],
    },
    {
      id: "fair-dealing",
      title: "Fair dealing & disputes",
      bullets: [
        "No collusion with recruiters to manipulate submissions, credits, or dispute outcomes.",
        "Disputes are resolved through the platform's documented process.",
        "False dispute claims may result in account restrictions.",
      ],
    },
    {
      id: "confidentiality",
      title: "Confidentiality",
      bullets: [
        "Recruiter pitches, internal notes, and pipeline data are confidential.",
        "Do not share recruiter identities or submission details outside your hiring team without need.",
      ],
    },
    {
      id: "enforcement",
      title: "Enforcement",
      bullets: [
        "AxioHire may suspend or terminate access for policy violations.",
        "The platform may audit hiring activity and fee compliance.",
        "Material term changes require re-acceptance before continued use.",
      ],
    },
  ],
};

export const affiliateTerms: RoleTerms = {
  roleLabel: "Affiliate",
  intro:
    "These terms govern your use of AxioHire as a sourcing affiliate. You refer candidates via your share link and may earn a sourcing fee when they are placed.",
  sections: [
    {
      id: "role",
      title: "Affiliate role",
      bullets: [
        "You source candidates into the AxioHire talent pool via your personal intake link.",
        "You do not submit candidates to jobs or run screening — that is handled by verified recruiters.",
        "You are an independent contractor, not an employee of AxioHire.",
      ],
    },
    {
      id: "integrity",
      title: "Candidate integrity",
      bullets: [
        "You must not fabricate candidate profiles or coerce sign-ups under false pretenses.",
        "Candidates must consent to joining the talent pool via the public form.",
        "Fraudulent referrals may result in forfeiture of sourcing fees and account removal.",
      ],
    },
    {
      id: "attribution",
      title: "Attribution & payouts",
      bullets: [
        "Sourcing credit follows first valid attribution rules on the platform.",
        "Sourcing fees are paid when a referred candidate is confirmed hired, subject to guarantee windows.",
        "TDS and other statutory deductions apply as per applicable law.",
      ],
    },
    {
      id: "conduct",
      title: "Conduct",
      bullets: [
        "Do not circumvent AxioHire by arranging off-platform placements for candidates introduced via your link.",
        "Do not spam or mislead candidates about roles or compensation.",
        "AxioHire may suspend affiliate accounts for policy violations.",
      ],
    },
    {
      id: "kyc",
      title: "KYC & identity",
      bullets: [
        "You must complete the same identity pack as recruiters (selfie, Aadhaar, PAN) before share links and payouts unlock.",
        "Client names stay redacted until an admin verifies your documents.",
        "You must provide truthful KYC details; one affiliate identity per person.",
      ],
    },
  ],
};

export const clientTerms: RoleTerms = {
  roleLabel: "Client company",
  intro:
    "These terms govern your use of AxioHire as a hiring company. You agree to post legitimate roles, complete company KYC, protect candidate data, and use the platform fairly.",
  sections: hrTerms.sections.filter((s) => s.id !== "conflict"),
};

export const consultancyTerms: RoleTerms = {
  roleLabel: "Consultancy",
  intro:
    "These terms govern your use of AxioHire as a consultancy. You agree to complete organisation KYC, invite only genuine recruiters, and follow marketplace conduct rules.",
  sections: [
    {
      id: "authority",
      title: "Role & authority",
      bullets: [
        "You represent a legitimate recruitment consultancy and have authority to invite recruiters.",
        "Information you provide (legal name, company PAN, signatory) must be accurate and current.",
        "Misrepresentation may result in access revocation and withheld payouts.",
      ],
    },
    {
      id: "marketplace",
      title: "Marketplace access",
      bullets: [
        "Unmasked client names unlock after AxioHire verifies your company documents.",
        "You must not scrape, republish, or share client job data off-platform.",
        "Recruiters you invite remain responsible for their own individual KYC.",
      ],
    },
    ...hrTerms.sections.filter((s) => s.id !== "authority" && s.id !== "conflict"),
  ],
};
